import React from 'react';
import cx from 'classnames';

import {
  etapasDaNotificacao,
  resumoDasEtapas,
} from 'volto-ministerio-publico-federal/helpers/Notificacoes';
import type { FluxoNotificacao } from 'volto-ministerio-publico-federal/helpers/Notificacoes';

type Props = {
  estado: string | null | undefined;
  fluxo?: FluxoNotificacao;
  estadoAnterior?: string | null;
  /** No painel só os marcadores + o resumo; na página, com os rótulos. */
  compacto?: boolean;
  /**
   * Etapa seguinte que ainda é só uma possibilidade (ex.: a publicação
   * nacional, opcional): entra pontilhada ao fim da trilha, com a ação que a
   * inicia embaixo do rótulo.
   */
  proximaEtapa?: { rotulo: string; acao: React.ReactNode };
};

/**
 * Barra de progresso do fluxo: em que etapa a notícia (ou página) estava
 * quando a notificação foi gerada. O resumo em texto ("Etapa 2 de 7: Revisão
 * de texto") é o que o leitor de tela lê; os marcadores são decorativos.
 */
const EtapasFluxo = ({
  estado,
  fluxo = 'noticia',
  estadoAnterior,
  compacto = true,
  proximaEtapa,
}: Props) => {
  const etapas = etapasDaNotificacao(estado, fluxo, estadoAnterior);
  const resumo = resumoDasEtapas(etapas);
  if (!resumo) return null;
  // Fluxo concluído: a última etapa fica feita e nenhuma está em curso.
  const concluido =
    etapas[etapas.length - 1]?.situacao === 'feita' &&
    !etapas.some((e) => e.situacao === 'atual' || e.situacao === 'revogada');

  return (
    <div
      className={cx('mpf-notificacoes__etapas', {
        'mpf-notificacoes__etapas--compacto': compacto,
      })}
    >
      {/* Só os marcadores são decorativos (aria-hidden por etapa): a ação da
          próxima etapa precisa continuar acessível. */}
      <ol className="mpf-notificacoes__etapas-lista">
        {etapas.map((etapa, indice) => (
          <li
            key={etapa.id}
            aria-hidden="true"
            className={cx(
              'mpf-notificacoes__etapa',
              `mpf-notificacoes__etapa--${etapa.situacao}`,
              {
                'mpf-notificacoes__etapa--concluida':
                  concluido && indice === etapas.length - 1,
              },
            )}
            title={etapa.rotulo}
          >
            <span className="mpf-notificacoes__etapa-marcador" />
            {!compacto && (
              <span className="mpf-notificacoes__etapa-rotulo">
                {etapa.rotulo}
              </span>
            )}
          </li>
        ))}
        {proximaEtapa && (
          <li className="mpf-notificacoes__etapa mpf-notificacoes__etapa--proxima">
            <span className="mpf-notificacoes__etapa-marcador" aria-hidden />
            {!compacto && (
              <span className="mpf-notificacoes__etapa-rotulo" aria-hidden>
                {proximaEtapa.rotulo}
              </span>
            )}
            {proximaEtapa.acao}
          </li>
        )}
      </ol>
      <p className="mpf-notificacoes__etapas-resumo">{resumo}</p>
    </div>
  );
};

export default EtapasFluxo;
